import { useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { ViewMode } from '@playbooks/action-cards-utils/src'
import { Toaster } from '@playbooks/ui/components/sonner'
import { cn } from '@playbooks/ui/lib/utils'
import { ActionCardsElement } from '@playbooks/action-cards'
import { ActionCard } from '@playbooks/core/src/interfaces/ActionCard'

import Footer from './components/Footer'
import SubmitForm from './components/SubmitForm'
import ProgressBar from './components/ProgressBar'
import useMountEffect from './hooks/useMountEffect'
import { ON_ACTION_CARD_SAVE_EVENT } from './constants'
import ActionCardsView from './components/ActionCardsView'
import SliderNavigation from './components/SliderNavigation'
import { FileUploadServiceProvider } from './services/fileUploadService'
import { setTrackFunction, trackRequested } from './services/trackService'
import { parseCompletionKeywords } from './services/completionKeywordsService'
import { clearLocalData, saveLocalData, setIsStorageActive } from './services/storage'
import type { WebPlaySettings, SubmissionService, WebPlayConfig, ActionCardsDataProvider, SliderManager } from './types'
import { getHighchartSvg } from './services/scoringDimensionsService'
import getAnonymousToken from './utils/getAnonymousToken'
import ConfirmDialog from './components/ConfirmDialog'
import useEmailControl from './hooks/useEmailControl'
import useCompletionKeywordsMatch from './hooks/useCompletionKeywordsMatch'

interface WebPlayProps {
  playbookId: string
  showScore: boolean
  config: WebPlayConfig
  settings: WebPlaySettings
  actionCard: ActionCard
  submissionService: SubmissionService
  trackFn?: (event: string, data: Record<string, unknown>) => void
}

/**
 * WebPlay component
 *
 * @param {object} props.actionCard - Loaded action card JSON data
 * @param {object} props.settings - WebPlay settings from Playbook
 * @param {object} props.submissionService - Object with submission methods: createPlay, attachFileToPlay, attachActionCardToPlay, sendPlayCreatedNotification, rollbackPlayCreation, createAnonymousToken
 * @param {object} props.config - { serviceBaseUrl, playbooksBackendUrl }
 * @param {string} props.playbookId - Playbook ID for which the WebPlay is rendered
 * @param {string} [props.email] - Default user email if known
 * @param {boolean} [props.showScore] - Whether to show score in the WebPlay
 * @param {function} [props.trackFn] - Optional analytics track function (event, data) => void
 */
export default function WebPlay({
  config,
  trackFn,
  settings,
  showScore,
  actionCard,
  playbookId,
  submissionService,
}: WebPlayProps) {
  const [progress, setProgress] = useState(0)
  const [isSheetOpen, setIsSheetOpen] = useState(false)
  const [isStorageEnabled, setIsStorageEnabled] = useState(true)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [totalScore, setTotalScore] = useState<number>(actionCard.score || 0)

  const actionCardsData = actionCard
  const webplayContainerRef = useRef<HTMLDivElement>(null)

  const { t } = useTranslation()

  const handleConfirmReset = async () => {
    setShowConfirmDialog(false)
    await clearLocalData(playbookId)
    window.location.reload()
  }

  const handleCancelReset = () => {
    setShowConfirmDialog(false)
  }

  const [lastSlideHtml, setLastSlideHtml] = useState<string>(() => {
    // Replace the placeholder with the actual Highchart HTML in the final slide layout on the initial load
    const html =
      settings.finalSlideLayout?.replace('{{scoring-chart}}', getHighchartSvg(actionCardsData, settings.isDarkTheme)) ||
      ''

    return html
  })

  // Initialization of the finish button visibility state based on the enableFunnelView setting
  const [isFinishButtonVisible, setIsFinishButtonVisible] = useState(!settings.enableFunnelView)

  const [anonymousToken, setAnonymousToken] = useState('')

  const sliderManagerRef = useRef<SliderManager | null>(null)
  const actionCardsElementRef = useRef<ActionCardsElement | null>(null)

  const { enableFunnelView, enableDocumentView, finalSlideLayout } = settings

  const hasSections = !!actionCardsData?.sections?.length
  const viewMode = enableFunnelView ? ViewMode.FUNNEL : enableDocumentView ? ViewMode.DOC : ViewMode.DEFAULT

  const completionKeywords = useMemo(() => parseCompletionKeywords(settings), [settings])

  const { loggedInUserEmail } = settings

  const {
    isInvalid,
    webPlayEmail,
    isPolicyAccepted,
    isWebPlayEmailValid,
    isWebPlayEmailInputVisible,
    setWebPlayEmail,
    syncWebPlayEmail,
    setIsPolicyAccepted,
  } = useEmailControl({
    playbookId,
    loggedInUserEmail,
    actionCardsElementRef,
  })

  const { isCompletionKeywordsMatched, adjustCompletionKeywordsState } = useCompletionKeywordsMatch({
    completionKeywords,
    actionCardsElementRef,
  })

  // Convert webplay settings to action cards settings
  const { actionCardsSettings, actionCardsCallbacks, actionCardsConfig } = useMemo<ActionCardsDataProvider>(() => {
    return {
      actionCardsSettings: {
        viewMode,
        hideLock: true,
        isWebplay: true,
        hideScore: !showScore,
        showMandatoryFields: true,
        hideSeparatorsAbility: true,
        headers: settings.headersCount,
        accentColor: settings.accentColor,
        hideFlow: settings.enableFunnelView,
        lastSlideHtml,
      },
      actionCardsCallbacks: {
        onLastSlideVisible: () => {
          setIsSheetOpen(true)
        },
        // This callback is triggered on every change in the action card,
        // so we update the local data and the state with the latest action card data here
        onChange: async value => {
          const clonedValue = JSON.parse(JSON.stringify(value)) as ActionCard
          // Inserting the spider chart svg into the final slide html if there is the {{scoring-chart}} replacer
          const html =
            finalSlideLayout?.replace('{{scoring-chart}}', getHighchartSvg(clonedValue, settings.isDarkTheme)) || ''
          // Set the final slide html in the component state
          setLastSlideHtml(html)
          // Save action cards data in the actionCardDbStore
          await saveLocalData(clonedValue, playbookId)
          // The event is needed to trigger the update of the action cards data in the third-party scripts
          dispatchEvent(new CustomEvent(ON_ACTION_CARD_SAVE_EVENT, { detail: clonedValue }))
          // Synchronize the email in the webplay state with the email in the action card
          syncWebPlayEmail()
          // Check if the completion keywords are matched and update the state accordingly to show/hide the finish button
          adjustCompletionKeywordsState()
          // Update the action cards total score
          setTotalScore(clonedValue.score || 0)
        },
      },
      actionCardsConfig: {
        serviceAuthToken: anonymousToken,
        recommendPlaybooks: async () => [],
        serviceBaseUrl: config.serviceBaseUrl,
        playbooksBackendUrl: config.playbooksBackendUrl,
        fileUploadServiceProvider: new FileUploadServiceProvider(playbookId),
      },
    }
  }, [
    syncWebPlayEmail,
    adjustCompletionKeywordsState,
    anonymousToken,
    config.playbooksBackendUrl,
    config.serviceBaseUrl,
    finalSlideLayout,
    lastSlideHtml,
    playbookId,
    settings.accentColor,
    settings.enableFunnelView,
    settings.headersCount,
    settings.isDarkTheme,
    showScore,
    viewMode,
  ])

  const settingsWithLastSlide = {
    ...actionCardsSettings,
    lastSlideHtml,
  }

  useMountEffect(() => {
    getAnonymousToken(submissionService.createAnonymousToken).then(setAnonymousToken)

    if (trackFn) {
      setTrackFunction(trackFn)
      trackRequested(playbookId)
    }

    if (!settings.enableFunnelView) return

    const carouselElements = webplayContainerRef.current?.querySelector('[data-role="webplay-carousel"]')
    const scrollContainer = webplayContainerRef.current?.querySelector(
      '[data-role="data-action-cards-sides-scroll-container"]',
    )

    // Observe '[data-role="data-action-cards-sides-scroll-container"]' scroll width changed to update the progress bar in funnel view
    const mutationObserver = new MutationObserver(() => {
      if (carouselElements) {
        const scrollWidth = carouselElements.scrollWidth - carouselElements.clientWidth
        const currentProgress = (carouselElements.scrollLeft / scrollWidth) * 100

        setProgress(currentProgress)
      }
    })

    if (scrollContainer) {
      mutationObserver.observe(scrollContainer, { childList: true })
    }

    const handleCarouselScroll = (event: Event) => {
      const target = event.target as HTMLElement

      const scrollWidth = target.scrollWidth - target.clientWidth
      const currentProgress = (target.scrollLeft / scrollWidth) * 100

      setProgress(currentProgress)
    }

    carouselElements?.addEventListener('scroll', handleCarouselScroll)

    return () => {
      carouselElements?.removeEventListener('scroll', handleCarouselScroll)
      mutationObserver.disconnect()
    }
  })

  const handleStorageEnabling = (isEnabled: boolean) => {
    setIsStorageEnabled(isEnabled)
    setIsStorageActive(isEnabled)
  }

  const handleFinishButtonVisibility = (isVisible: boolean) => {
    if (settings.enableFunnelView) {
      setIsFinishButtonVisible(isVisible)
    }
  }

  return (
    <div
      ref={webplayContainerRef}
      data-role="webplay-container"
      className="w-full h-full flex flex-col items-center justify-center"
    >
      {createPortal(
        <Toaster position="bottom-center" richColors theme={settings.isDarkTheme ? 'dark' : 'light'} />,
        document.body,
      )}
      {enableFunnelView && (
        <div className="flex w-full px-4 mb-3">
          <ProgressBar progress={progress} accentColor={settings.accentColor} />
        </div>
      )}
      <div className="flex flex-col flex-1 w-full overflow-hidden">
        <ActionCardsView
          actionCard={actionCardsData}
          playbookId={playbookId}
          config={actionCardsConfig}
          settings={settingsWithLastSlide}
          callbacks={actionCardsCallbacks}
          sliderManagerRef={sliderManagerRef}
          actionCardsElementRef={actionCardsElementRef}
          setIsFinishButtonVisible={handleFinishButtonVisibility}
        />
      </div>

      <div
        className={cn(
          'relative z-20 flex flex-col w-full shadow-top border-t border-border px-4 pb-2',
          settings.enableFunnelView ? 'pt-4 gap-y-4' : 'pt-2',
        )}
      >
        <SubmitForm
          viewMode={viewMode}
          isOpen={isSheetOpen}
          showScore={showScore}
          playbookId={playbookId}
          webplaySettings={settings}
          setIsOpen={setIsSheetOpen}
          totalScore={totalScore}
          isStorageEnabled={isStorageEnabled}
          submissionService={submissionService}
          completionKeywords={completionKeywords}
          setIsStorageEnabled={handleStorageEnabling}
          actionCardsElementRef={actionCardsElementRef}
          isInvalid={isInvalid}
          webPlayEmail={webPlayEmail}
          setWebPlayEmail={setWebPlayEmail}
          isPolicyAccepted={isPolicyAccepted}
          setIsPolicyAccepted={setIsPolicyAccepted}
          isWebPlayEmailValid={!!isWebPlayEmailValid}
          isWebPlayEmailInputVisible={isWebPlayEmailInputVisible}
        />

        <SliderNavigation
          viewMode={viewMode}
          hasSections={hasSections}
          accentColor={settings.accentColor}
          openSheet={() => setIsSheetOpen(true)}
          sliderManagerRef={sliderManagerRef}
          actionCardsElementRef={actionCardsElementRef}
          isFinishButtonVisible={isFinishButtonVisible && isCompletionKeywordsMatched}
          webplaySettings={settings}
        />

        <Footer setShowConfirmDialog={setShowConfirmDialog} />
      </div>
      {showConfirmDialog && (
        <ConfirmDialog
          title={t('footer.reset_confirm_title', 'Reset Progress')}
          message={t(
            'footer.reset_confirm_message',
            'Are you sure you want to reset your progress? All your data will be cleared.',
          )}
          onConfirm={handleConfirmReset}
          onCancel={handleCancelReset}
          confirmText={t('footer.reset_confirm_button', 'Reset')}
          cancelText={t('footer.reset_cancel_button', 'Cancel')}
        />
      )}
    </div>
  )
}
